import { MantineProvider, Menu } from "@mantine/core";
import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";

import { Pagination } from "../../../components/Pagination";
import { RpaTableMenu, createRpaTableMenuColumn } from "../../../components/RpaTableMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { Heading } from "../../../components/Typography";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { openApiProductColumns } from "../../../configuration/columns/openApiProduct.column";
import { openApiProductFilters } from "../../../configuration/filters/openApiProduct.filter";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";

export const OpenApiProductsView: React.FC = () => {
  const { classes } = usePageShellStyles();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.OpenApiProductsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.OpenApiProductsQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.OpenApiProductsQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const { data, isLoading, error, refetch } = GraphQL.useOpenApiProductsQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.openapiProducts.totalCount),
    }
  );
  const openApiProducts = data?.openapiProducts.data;

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={classes.container} data-testid="catalog-view" aria-label="Catalog view">
        <Heading as="h1" size="6xl">
          Catalog
        </Heading>
        <div className={classes.row}>
          <RpiSearchFilter
            placeholder="Search by Name"
            filters={openApiProductFilters}
            onChange={setSearchInputs}
            onSearch={setSearchGeneral}
          />
        </div>
        <RpiTableContainer
          data-testid="open-api-products-table"
          footer={<Pagination isLoading={isLoading} pagination={pagination} />}
        >
          <RpiTable
            emptyMessage={searchInputs?.length || searchGeneral ? "No products found." : "Products will appear here."}
            isLoading={isLoading}
            error={error}
            perPage={openApiProducts?.length}
            data={openApiProducts}
            columns={openApiProductColumns}
            additionalColumns={[
              createRpaTableMenuColumn<GraphQLPagedQueryData<GraphQL.OpenApiProductsQuery>>((row) => (
                <OpenApiProductsActionMenu skuId={row.id} />
              )),
            ]}
            withSortBy={{ onChange: setSortInput }}
          />
        </RpiTableContainer>
      </div>

      {outlet}
    </MantineProvider>
  );
};

type OpenApiProductsActionMenuProps = {
  skuId: number;
};

function OpenApiProductsActionMenu({ skuId }: OpenApiProductsActionMenuProps) {
  const navigateRoute = useNavigateRoute();
  return (
    <RpaTableMenu>
      <Menu.Item
        icon={<IconV3 icon="description" size={20} />}
        onClick={() => navigateRoute(["open-api-product", { id: skuId }])}
      >
        View Details
      </Menu.Item>
    </RpaTableMenu>
  );
}
